import { useEffect, useState } from 'react'
import type { DetectedPrompt, PromptOption } from '../api/client'
import { Icon } from './Icon'

// Pinned card shown above the input bar whenever the agent is asking something in
// its terminal (permission box, plan/skill menu, y/n, multi-select). Lets you answer by
// TAPPING instead of typing a key into a TUI you can't reach on mobile. Tapping writes the
// option's keystrokes back to the PTY via the normal input path. Free-text options (Claude's
// "Type something.") are NOT shown here — the chat composer below already takes free text
// straight to the PTY, so a second input box on the card would be redundant.
interface InteractivePromptCardProps {
  prompt: DetectedPrompt
  answering?: boolean
  onAnswer: (opt: PromptOption) => void
  onDismiss?: () => void
  /** Collapse the card into a small restore pill (keeps the prompt around). */
  onHide?: () => void
  /** Ask the server to re-scan the terminal for the current prompt. */
  onRescan?: () => void
}

// Multi-select (checkbox list). The CHECKLIST scrolls on its own; the Submit button + any action
// options (e.g. "Chat about this") stay pinned below it. A tap sends the option's DIGIT — Claude
// ignores programmatic arrow keys but a digit toggles that exact checkbox (absolute, so it can
// never hit the wrong/extra row — the bug the old arrow-nav had), and selects an action option.
// Submit moves to the `✔ Submit` tab (→, which works for the horizontal tab nav) + Enter. Local
// `checked` is optimistic, re-seeded when a new prompt arrives (stable multi id — see patterns.ts).
function MultiPromptOptions({ prompt, answering, onAnswer }: {
  prompt: DetectedPrompt
  answering?: boolean
  onAnswer: (opt: PromptOption) => void
}) {
  const visible = prompt.options.filter((o) => !o.input)   // drop "Type something" (composer handles free text)
  const checks = visible.filter((o) => o.checked !== undefined)
  const actions = visible.filter((o) => o.checked === undefined)
  const [checked, setChecked] = useState<Record<string, boolean>>(
    () => Object.fromEntries(checks.map((o) => [o.key, !!o.checked])),
  )
  useEffect(() => {
    setChecked(Object.fromEntries(
      prompt.options.filter((o) => !o.input && o.checked !== undefined).map((o) => [o.key, !!o.checked]),
    ))
  }, [prompt.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (opt: PromptOption) => {
    setChecked((prev) => ({ ...prev, [opt.key]: !prev[opt.key] }))
    onAnswer(opt)                                   // send = the digit (toggles this exact checkbox)
  }
  const pick = (opt: PromptOption) => onAnswer(opt) // action option: digit selects it (e.g. "Chat about this")
  const submit = () => onAnswer({ key: '__submit__', label: 'Submit', send: prompt.submitSend ?? '\r' })

  return (
    <>
      <div className="iprompt-checklist">
        {checks.map((opt, i) => (
          <button
            key={`${opt.key}-${i}`}
            className={`iprompt-btn iprompt-btn--check${checked[opt.key] ? ' is-checked' : ''}`}
            disabled={answering}
            onClick={() => toggle(opt)}
            type="button"
            aria-pressed={!!checked[opt.key]}
          >
            <span className="iprompt-check" aria-hidden>✓</span>
            <span className="iprompt-btn-label">
              {opt.label}
              {opt.desc && <span className="iprompt-btn-desc">{opt.desc}</span>}
            </span>
          </button>
        ))}
      </div>
      <div className="iprompt-multi-footer">
        <button className="iprompt-btn iprompt-submit" disabled={answering} onClick={submit} type="button">
          <Icon name="send" size={15} />
          <span className="iprompt-btn-label">Submit</span>
        </button>
        {actions.map((opt, i) => (
          <button
            key={`act-${opt.key}-${i}`}
            className="iprompt-btn"
            disabled={answering}
            onClick={() => pick(opt)}
            type="button"
          >
            <span className="iprompt-btn-label">
              {opt.label}
              {opt.desc && <span className="iprompt-btn-desc">{opt.desc}</span>}
            </span>
          </button>
        ))}
      </div>
    </>
  )
}

export function InteractivePromptCard({
  prompt,
  answering,
  onAnswer,
  onDismiss,
  onHide,
  onRescan,
}: InteractivePromptCardProps) {
  return (
    <div className="iprompt-card" role="group" aria-label="Interactive prompt">
      <div className="iprompt-head">
        <span className="iprompt-badge">ตอบในแชท</span>
        <span className="iprompt-question">{prompt.question}</span>
        {onRescan && (
          <button className="iprompt-dismiss" onClick={onRescan} title="สแกนหน้าจอใหม่" aria-label="Rescan prompt" type="button">
            <Icon name="refresh" size={15} />
          </button>
        )}
        {onHide && (
          <button className="iprompt-dismiss" onClick={onHide} title="ย่อการ์ดนี้" aria-label="Hide prompt" type="button">
            <Icon name="chevron-down" size={15} />
          </button>
        )}
        {onDismiss && (
          <button className="iprompt-dismiss" onClick={onDismiss} title="ซ่อนการ์ดนี้" aria-label="Dismiss prompt" type="button">
            <Icon name="x" size={15} />
          </button>
        )}
      </div>
      {prompt.multi ? (
        <MultiPromptOptions prompt={prompt} answering={answering} onAnswer={onAnswer} />
      ) : (
        <div className="iprompt-options">
          {prompt.options.filter((opt) => !opt.input).map((opt, i) => (
            <button
              key={`${opt.key}-${i}`}
              className={`iprompt-btn${i === 0 ? ' iprompt-btn--primary' : ''}`}
              disabled={answering}
              onClick={() => onAnswer(opt)}
              type="button"
            >
              {prompt.kind === 'numbered' && <span className="iprompt-btn-key">{opt.key}</span>}
              <span className="iprompt-btn-label">
                {opt.label}
                {opt.desc && <span className="iprompt-btn-desc">{opt.desc}</span>}
              </span>
            </button>
          ))}
        </div>
      )}
      {answering && <div className="iprompt-answering">กำลังส่ง...</div>}
      {prompt.raw && prompt.raw.length > 0 && (
        <details className="iprompt-raw">
          <summary>raw (debug)</summary>
          <pre>{prompt.raw.join('\n')}</pre>
        </details>
      )}
    </div>
  )
}
